import React from 'react';
import { STOP_TYPE } from '../redux/api/load/load.model';
import DateUtils from '../utils/dateUtils';
import { InvoiceDescriptionSettings, TenantAccountingSettings } from '../providers/User/User.model';
import { Order, OrderCommodities } from '../redux/api/orders/orders.model';
import { TripStop } from '../redux/api/trips/trips.model';
import { useCommoditySettings } from './useCommoditySettings';
import { formatDimensionsV2, getUnitLabel } from '../utils/commodity/commodityUtils';

const useInvoiceDefaultDescription = (): [
  (accountingSettings: TenantAccountingSettings, isCommoditySettingEnabled: boolean) => (load: Order) => string,
] => {
  const {
    isDimensionsEnabled,
    isUnitEnabled,
    isCommodityIdEnabled,
    isMakeEnabled,
    isModelEnabled,
    measurementSystem
  } = useCommoditySettings();

  const buildCommodityDescription = (commodity: OrderCommodities): string => {
    const parts: string[] = [];
    const commodityReadableId =
      isCommodityIdEnabled && commodity.readable_commodity_id
        ? commodity.readable_commodity_id
        : '';
    if (isMakeEnabled && commodity.make) parts.push(`Make: ${commodity.make}`);
    if (isModelEnabled && commodity.model) parts.push(`Model: ${commodity.model}`);
    if (isUnitEnabled && commodity.unit) parts.push(`Unit: ${getUnitLabel(commodity.unit)}`);
    if (isDimensionsEnabled) {
      const dim = formatDimensionsV2(
        commodity.length ?? null,
        commodity.width ?? null,
        commodity.height ?? null,
        measurementSystem
      );
      if (dim) parts.push(dim);
    }
    // Always include quantity and weight
    parts.unshift(`Wt: ${commodity.weight}`);
    parts.unshift(`Qty: ${commodity.quantity}`);
    return `${commodity.description} ${commodityReadableId} ${
      parts.length ? '(' + parts.join(', ') + ')' : ''
    }`;
  };

  const buildStopDescription = (
    stop: TripStop,
    descriptionSettings: InvoiceDescriptionSettings
  ): string => {
    const { show_load_dates, show_pickup_and_delivery_numbers, show_stop_address, show_stop_name } =
      descriptionSettings;
    const { start_date, stop_identifier } = stop;
    const stopName = stop.location?.name ? stop.location?.name : '';
    const stopAddress = stop.location?.full_address || '';

    const addressElements: string[] = [];
    if (show_stop_name && stopName) addressElements.push(stopName);
    if (show_stop_address && stopAddress) addressElements.push(stopAddress);

    const addressString = addressElements.length > 0 ? addressElements.join(', ') : '';

    const stopType =
      stop.stop_type === STOP_TYPE.PICKUP
        ? 'Pickup'
        : stop.stop_type === STOP_TYPE.DROP_OFF
        ? 'Delivery'
        : stop.stop_type === STOP_TYPE.TRANSFER
        ? 'Transfer'
        : '';

    // Print data if setting is on and data not null. Add a comma at the end of each string.
    const identifier =
      show_pickup_and_delivery_numbers && stop_identifier ? `${stopType} #${stop_identifier}` : '';
    const date = show_load_dates && start_date ? DateUtils.getMediumDate(new Date(start_date)) : '';

    const descriptionPrefix =
      stop.stop_type === STOP_TYPE.PICKUP
        ? 'Pick'
        : stop.stop_type === STOP_TYPE.DROP_OFF
        ? 'Drop'
        : stop.stop_type === STOP_TYPE.TRANSFER
        ? 'Transfer'
        : 'Stop';

    const descriptionSuffix = [date, addressString, identifier].filter((el) => el).join(', ');
    return `${descriptionPrefix}: ${descriptionSuffix}`;
  };

  const invoiceDefaultDescriptionMaker =
    (accountingSettings: TenantAccountingSettings, isCommoditySettingEnabled: boolean) =>
    (load: Order): string => {
      const {
        show_customer_load_number,
        show_drop_off_stops,
        show_pickup_stops,
        show_transfer_stops,
        show_truckbase_load_number,
        show_unspecific_stops,
        show_commodities,
        show_trucks,
        show_trailers,
        show_drivers,
        show_total_empty_miles,
        show_total_loaded_miles,
        show_tags,
        show_order_notes
      } = accountingSettings.invoice_description;
      let listOfAllStops = '';
      let listOfCommmodities = '';

      if (show_commodities && isCommoditySettingEnabled) {
        listOfCommmodities = load.order_commodities
          .map((commodity) => buildCommodityDescription(commodity))
          .join(', ');
      }

      const intermediateStopDescriptions = (load.full_route_stop_overview || []).map((stop) => {
        // Return stop description only if setting is enabled for the stop type
        if (
          (show_pickup_stops && stop.stop_type === STOP_TYPE.PICKUP) ||
          (show_drop_off_stops && stop.stop_type === STOP_TYPE.DROP_OFF) ||
          (show_transfer_stops && stop.stop_type === STOP_TYPE.TRANSFER) ||
          (show_unspecific_stops && stop.stop_type === null)
        ) {
          return buildStopDescription(stop, accountingSettings.invoice_description);
        }
      });

      listOfAllStops = [...intermediateStopDescriptions]
        .filter((stop) => !!stop && stop !== '')
        .join('\n\n');

      const loadNumber =
        show_truckbase_load_number && show_customer_load_number && load.customer_order_number
          ? `Order: #${load.readable_id}, Customer order: #${load.customer_order_number}`
          : show_truckbase_load_number
          ? `Order #${load.readable_id}`
          : show_customer_load_number && load.customer_order_number
          ? `Customer order #${load.customer_order_number}`
          : '';

      const commodities = listOfCommmodities ? `Commodities: ${listOfCommmodities}` : '';

      const allTrucks: Set<string> = new Set();
      const allTrailers: Set<string> = new Set();
      const allDrivers: Set<string> = new Set();
      let totalEmptyMiles = 0;
      let totalLoadedMiles = 0;

      load.trips.forEach((trip) => {
        if (trip.truck?.name) allTrucks.add(trip.truck.name);
        if (trip.trailer?.name) allTrailers.add(trip.trailer.name);
        trip.drivers.forEach((driver) => {
          if (driver.name) allDrivers.add(driver.name);
        });
        if (trip.empty_miles) totalEmptyMiles += trip.empty_miles;
        if (trip.estimated_mileage) totalLoadedMiles += trip.estimated_mileage;
      });

      const descriptionItems = [];
      if (loadNumber) descriptionItems.push(loadNumber);
      if (show_trucks && allTrucks.size > 0)
        descriptionItems.push(`Truck(s): ${[...allTrucks].join(', ')}`);
      if (show_trailers && allTrailers.size > 0)
        descriptionItems.push(`Trailer(s): ${[...allTrailers].join(', ')}`);
      if (show_drivers && allDrivers.size > 0)
        descriptionItems.push(`Driver(s): ${[...allDrivers].join(', ')}`);
      if (show_total_loaded_miles) descriptionItems.push(`Total loaded miles: ${totalLoadedMiles}`);
      if (show_total_empty_miles) descriptionItems.push(`Total empty miles: ${totalEmptyMiles}`);
      if (listOfAllStops) descriptionItems.push(listOfAllStops);
      if (show_tags && load.tags?.length > 0)
        descriptionItems.push(`Tag(s): ${load.tags?.map((tag) => tag.name).join(', ')}`);
      if (commodities) descriptionItems.push(commodities);
      if (show_order_notes && load.instructions)
        descriptionItems.push(`Order Notes: ${load.instructions}`);

      return descriptionItems.join('\n\n');
    };

  return [invoiceDefaultDescriptionMaker];
};

export default useInvoiceDefaultDescription;
